import { useQueryClient } from '@tanstack/react-query';
import klydoApiClient from '../../../api/klydo';
import useKlydoMutation, {
  UseKlydoMutationOptions,
} from '../../../hooks/useKlydoMutation';
import useAppContext from '../../../hooks/useAppContext';
import { AlertType } from '../../../design-system/DSAlert';
import { PUBLICATION_DATE_UPDATED_SUCCESS } from '../../../consts/alerts';

export type UseSetPublicationDateMutationParams = {
  klydoId: string;
  publicationTimestamp: number;
};

/**
 * Sets the go-live date of a klydo whose publication request is already APPROVED
 * (reschedule / publish-now / unpublish). The api rejects anything else, so never
 * wire this to a klydo without an approved request — use the submit flow instead.
 */
const useSetPublicationDateMutation = (
  options?: UseKlydoMutationOptions<
    void,
    UseSetPublicationDateMutationParams,
    void
  >,
) => {
  const queryClient = useQueryClient();
  const { showAlert } = useAppContext();

  return useKlydoMutation({
    // `options` is spread FIRST and the hook's own handlers wrap the caller's, so a
    // caller passing `onSettled` cannot silently drop the cache invalidation, and
    // one passing `onSuccess` cannot drop the toast.
    ...options,
    mutationKey: ['set-publication-date'],
    mutationFn: ({ klydoId, publicationTimestamp }) => {
      return klydoApiClient.setPublicationDate(klydoId, publicationTimestamp);
    },
    onSuccess: (data, variables) => {
      showAlert({
        message: PUBLICATION_DATE_UPDATED_SUCCESS,
        alertType: AlertType.SUCCESS,
      });
      options?.onSuccess?.(data, variables);
    },
    onSettled: (data, error, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['klydo', variables.klydoId],
      });
      options?.onSettled?.(data, error, variables);
    },
  });
};

export default useSetPublicationDateMutation;
